import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { adminService } from '../services/adminService';

const ThreadDetail = () => {
  const { threadId } = useParams();
  const navigate = useNavigate();
  const [thread, setThread] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [includeMetadata, setIncludeMetadata] = useState(false);
  const [exporting, setExporting] = useState(false);

  const loadDetail = useCallback(async () => {
    if (!threadId) return;
    setLoading(true);
    setError(null);
    try {
      const data = await adminService.getThreadDetail(threadId, includeMetadata);
      setThread(data);
    } catch (err) {
      setError(err.message || 'Failed to load thread');
    } finally {
      setLoading(false);
    }
  }, [threadId, includeMetadata]);

  useEffect(() => {
    loadDetail();
  }, [loadDetail]);

  const handleExport = async () => {
    if (!threadId) return;
    setExporting(true);
    try {
      const data = await adminService.exportThread(threadId, includeMetadata);
      const blob = new Blob([JSON.stringify(data, null, 2)], {
        type: 'application/json',
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `thread-${threadId}.json`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      setError(err.message || 'Export failed');
    } finally {
      setExporting(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    try {
      return new Date(dateStr).toLocaleString();
    } catch {
      return dateStr;
    }
  };

  if (loading && !thread) {
    return <p className="text-gray-400">Loading thread...</p>;
  }

  if (error && !thread) {
    return (
      <div>
        <p className="text-red-400 mb-4">{error}</p>
        <button
          type="button"
          onClick={() => navigate('/admin')}
          className="text-blue-400 hover:text-blue-300"
        >
          Back to list
        </button>
      </div>
    );
  }

  return (
    <div className="max-w-4xl">
      <div className="flex items-center justify-between mb-4">
        <button
          type="button"
          onClick={() => navigate('/admin')}
          className="text-sm text-blue-400 hover:text-blue-300"
        >
          ← Back to threads
        </button>
        <div className="flex items-center gap-4">
          <label className="flex items-center gap-2 text-sm text-gray-400">
            <input
              type="checkbox"
              checked={includeMetadata}
              onChange={(e) => setIncludeMetadata(e.target.checked)}
              className="rounded border-gray-600 bg-gray-800 text-blue-500"
            />
            Include debug metadata
          </label>
          <button
            type="button"
            onClick={handleExport}
            disabled={exporting}
            className={
              'rounded-lg px-3 py-1.5 text-sm ' +
              (exporting
                ? 'bg-gray-700 text-gray-500 cursor-not-allowed'
                : 'bg-gray-700 text-gray-200 hover:bg-gray-600')
            }
          >
            {exporting ? 'Exporting...' : 'Export JSON'}
          </button>
        </div>
      </div>

      <h1 className="text-xl font-semibold text-gray-100 mb-2">
        {thread?.title || 'Untitled thread'}
      </h1>
      <p className="text-sm text-gray-400 mb-4">
        {thread?.user_email || thread?.user_id || '—'} · Updated{' '}
        {formatDate(thread?.updated_at)}
      </p>

      {thread?.summary && (
        <div className="mb-6 p-4 rounded-lg bg-gray-800/60 border border-gray-700">
          <h2 className="text-sm font-medium text-gray-300 mb-2">Summary</h2>
          <p className="text-gray-300 text-sm">{thread.summary.summary_text}</p>
          {thread.summary.key_topics?.length > 0 && (
            <p className="text-xs text-gray-500 mt-2">
              Topics: {thread.summary.key_topics.join(', ')}
            </p>
          )}
        </div>
      )}

      <h2 className="text-sm font-medium text-gray-300 mb-2">Messages</h2>
      <div className="space-y-3">
        {(thread?.messages || []).map((msg) => (
          <div
            key={msg.id}
            className={
              'p-4 rounded-lg border ' +
              (msg.sender === 'user'
                ? 'bg-gray-800/40 border-gray-700'
                : 'bg-gray-800/60 border-gray-600')
            }
          >
            <div className="flex justify-between items-start gap-2">
              <span className="text-xs font-medium text-gray-400">{msg.sender}</span>
              {msg.timestamp && (
                <span className="text-xs text-gray-500">{formatDate(msg.timestamp)}</span>
              )}
            </div>
            <p className="text-gray-200 text-sm mt-1 whitespace-pre-wrap">{msg.text}</p>
            {includeMetadata && msg.metadata && Object.keys(msg.metadata).length > 0 && (
              <pre className="mt-2 text-xs text-gray-500 overflow-x-auto">
                {JSON.stringify(msg.metadata, null, 2)}
              </pre>
            )}
          </div>
        ))}
      </div>

      {thread?.messages?.length === 0 && (
        <p className="text-gray-500 text-sm py-4">No messages in this thread.</p>
      )}
    </div>
  );
};

export default ThreadDetail;
